// ─────────────────────────────────────────────────────────────────────────────
// ApiService.js — Único punto de acceso a las APIs del ecosistema SIGMA
//
// Arquitectura de dos servidores:
//   · AUTH_URL (WebControl) — login y selección de empresa
//   · DATA_URL (API-SIGMA-CLOUD) — todos los datos del negocio
//
// Cada request autenticado lleva:
//   · x-api-key   → credencial de empresa, recibida en el login
//   · Bearer JWT  → credencial del usuario, recibida en el login
//   · ?db=tenant  → enruta al schema del gimnasio correcto
//
// Auth docs:  https://api.siax-system.net (WebControl)
// Data docs:  https://api-gateway-cloud.telcotronics.net/api-docs
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios'

const AUTH_URL = process.env.VUE_APP_AUTH_URL || 'https://api.siax-system.net'
const CENTINEL_URL = (process.env.VUE_APP_CENTINEL_URL && process.env.VUE_APP_CENTINEL_URL !== '')
  ? process.env.VUE_APP_CENTINEL_URL
  : 'https://api.telcotronics.com'
const CENTINEL_KEY = process.env.VUE_APP_CENTINEL_KEY || ''
// En dev: VUE_APP_DATA_URL='' → requests relativos al dev server → proxy a API-SIGMA-CLOUD (sin CORS)
// En prod: VUE_APP_DATA_URL='https://api-gateway-cloud.telcotronics.net' → directo al Gateway
const DATA_URL = (process.env.VUE_APP_DATA_URL !== undefined && process.env.VUE_APP_DATA_URL !== '')
  ? process.env.VUE_APP_DATA_URL
  : (process.env.NODE_ENV === 'production' ? 'https://api-gateway-cloud.telcotronics.net' : '')

function getTenant () {
  return localStorage.getItem('db_tenant') || ''
}

function getToken () {
  return localStorage.getItem('jwt_token') || ''
}

function getApiKey () {
  return localStorage.getItem('auth_api_key') || ''
}

function headers () {
  return {
    'x-api-key':     getApiKey(),
    'Authorization': `Bearer ${getToken()}`
  }
}

const ApiService = {

  // ── AUTENTICACIÓN (WebControl — api.siax-system.net) ──────────────────────

  // Paso 1: verifica credenciales y devuelve empresas del usuario.
  // Si el usuario tiene una sola empresa, Login.vue salta directo al paso 2.
  // Endpoint: POST /api/auth/empresas
  // Body:     { email, paswd }
  // Resp:     { success, unica, empresas: [{ dba, nombre, rol }] }
  async getEmpresas ({ email, paswd }) {
    const res = await axios.post(`${AUTH_URL}/api/auth/empresas`, {
      email: email.toLowerCase(),
      paswd
    })
    return res.data
  },

  // Paso 2: emite JWT y apiKey para la empresa seleccionada (o la única).
  // Endpoint: POST /api/auth/login
  // Body:     { email, paswd, dba }  — dba viene de la respuesta del paso 1
  // Resp:     { success, token, apiKey, db_tenant, usuario: { id, nombre, usuario, rol } }
  async login ({ email, paswd, dba }) {
    const res = await axios.post(`${AUTH_URL}/api/auth/login`, {
      email: email.toLowerCase(),
      paswd,
      dba
    })
    return res.data
  },

  // ── CRUD GENÉRICO (API-SIGMA-CLOUD — api-gateway-cloud.telcotronics.net) ──

  async get (ruta, params = {}) {
    const res = await axios.get(`${DATA_URL}/${ruta}`, {
      headers: headers(),
      params:  { db: getTenant(), ...params }
    })
    return res.data
  },

  async post (ruta, datos) {
    const res = await axios.post(`${DATA_URL}/${ruta}`, datos, {
      headers: headers(),
      params:  { db: getTenant() }
    })
    return res.data
  },

  async put (ruta, datos) {
    const res = await axios.put(`${DATA_URL}/${ruta}`, datos, {
      headers: headers(),
      params:  { db: getTenant() }
    })
    return res.data
  },

  async delete (ruta) {
    const res = await axios.delete(`${DATA_URL}/${ruta}`, {
      headers: headers(),
      params:  { db: getTenant() }
    })
    return res.data
  },

  // Búsqueda de clientes por RUC/cédula/nombre.
  async buscarClientes (consulta) {
    const res = await axios.get(`${DATA_URL}/api/clientes/consulta_clientesApps`, {
      headers: headers(),
      params:  { db: getTenant(), consulta }
    })
    return Array.isArray(res.data) ? res.data : []
  },

  // ── API_CENTINEL_SECURITY (api.telcotronics.com) ──────────────────────────
  // Control de acceso Hikvision. Auth solo con x-api-key (sin JWT ni tenant).
  // Docs: https://api.telcotronics.com/swagger-ui/index.html
  centinel: {
    _headers () {
      return { 'x-api-key': CENTINEL_KEY }
    },
    async get (path) {
      const res = await axios.get(`${CENTINEL_URL}${path}`, { headers: this._headers() })
      return res.data
    },
    async post (path, data) {
      const res = await axios.post(`${CENTINEL_URL}${path}`, data, { headers: this._headers() })
      return res.data
    },
    async postFull (path, data) {
      const res = await axios.post(`${CENTINEL_URL}${path}`, data, { headers: this._headers() })
      return { httpStatus: res.status, body: res.data }
    },
    async delete (path) {
      const res = await axios.delete(`${CENTINEL_URL}${path}`, { headers: this._headers() })
      return res.data
    }
  }
}

export default ApiService



// WEBPACK FOOTER //
// ./src/Servicios/ApiService.js